import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../../components/Shared/Modal';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import { PasswordCriteria } from '../../../components/Shared/PasswordCriteria';
import { isPasswordValid } from '../../../utils/password';
import { AlertCircle, Key, Mail, Phone } from 'lucide-react';

interface PasswordResetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onReset: (newPassword: string) => Promise<void>;
  employeeName: string;
  employeeEmail?: string;
  employeePhone?: string;
}

export function PasswordResetDialog({
  isOpen,
  onClose,
  onReset,
  employeeName,
  employeeEmail,
  employeePhone,
}: PasswordResetDialogProps) {
  const { t } = useTranslation('employees');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword) {
      setError(t('passwordResetDialog.errorRequired'));
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError(t('validation:password.invalid'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwordResetDialog.errorMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onReset(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      onClose();
    } catch {
      setError(t('passwordResetDialog.errorGeneral'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('passwordResetDialog.title')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {t('passwordResetDialog.notificationHeader', { name: employeeName })}
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('passwordResetDialog.notificationText')}
              </p>
              <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                {employeeEmail && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>{t('passwordResetDialog.viaEmail')} <strong>{employeeEmail}</strong></span>
                  </div>
                )}
                {employeePhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{t('passwordResetDialog.viaSms')} <strong>{employeePhone}</strong></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('passwordResetDialog.resetFor')}
          </p>
          <p className="text-base font-semibold text-gray-900 dark:text-white mt-1">
            {employeeName}
          </p>
        </div>

        {/* Password Fields */}
        <FormField label={t('passwordResetDialog.newPassword')} required error={error && !confirmPassword ? error : undefined}>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError('');
            }}
            placeholder={t('passwordResetDialog.newPasswordPlaceholder')}
            disabled={isSubmitting}
            autoFocus
          />
          <PasswordCriteria password={newPassword} />
        </FormField>

        <FormField label={t('passwordResetDialog.confirmPassword')} required error={error && confirmPassword ? error : undefined}>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            placeholder={t('passwordResetDialog.confirmPasswordPlaceholder')}
            disabled={isSubmitting}
          />
        </FormField>

        {/* General Error */}
        {error && newPassword && confirmPassword && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('passwordResetDialog.buttonCancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <Key className="w-4 h-4" />
            {isSubmitting ? t('passwordResetDialog.buttonResetting') : t('passwordResetDialog.buttonReset')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
